import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { useJobsStore, Job } from '../store/jobs'
import { listJobs, createJob, updateJob as updateJobApi } from '../api/client'
import CreateJobForm from '../components/CreateJobForm'
import Layout from '../components/Layout'
import SearchFilter from '../components/SearchFilter'
import BookmarkToggle from '../components/BookmarkToggle'
import RefreshButton from '../components/RefreshButton'
import LoadingButton from '../components/LoadingButton'
import ErrorMessage from '../components/ErrorMessage'

export default function Jobs() {
  const navigate = useNavigate()
  const { nocodbJobsViewUrl } = useAuthStore()
  const { jobs, setJobs, updateJob, loading, setLoading, error, setError } = useJobsStore()
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  // Filters
  const [search, setSearch] = useState('')
  const [bookmarkedOnly, setBookmarkedOnly] = useState(false)

  // Pagination
  const [nextPage, setNextPage] = useState<string | undefined>()
  const [currentPage, setCurrentPage] = useState<string | undefined>()
  const [pageHistory, setPageHistory] = useState<string[]>([])

  useEffect(() => {
    loadJobs()
  }, [refreshKey, currentPage])

  // Filter jobs client-side for search (API filtering is optional enhancement)
  const filteredJobs = jobs.filter((job) => {
    // Bookmarked filter
    if (bookmarkedOnly && !job.bookmarked) return false

    // Search filter (case-insensitive)
    if (search) {
      const searchLower = search.toLowerCase()
      const matchesTitle = job.title?.toLowerCase().includes(searchLower)
      const matchesPrompt = job.content_prompt.toLowerCase().includes(searchLower)
      const matchesNotes = job.notes?.toLowerCase().includes(searchLower)
      if (!matchesTitle && !matchesPrompt && !matchesNotes) return false
    }

    return true
  })

  async function loadJobs() {
    setLoading(true)
    setError(null)

    try {
      const response = await listJobs({ limit: 25, page: currentPage })
      setJobs(response.jobs as Job[])
      setNextPage(response.next_page)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load jobs')
    } finally {
      setLoading(false)
    }
  }

  async function handleCreateJob(data: Parameters<typeof createJob>[0]) {
    try {
      const job = await createJob(data) as Job
      setJobs([job, ...jobs])
      setShowCreateForm(false)
      navigate(`/jobs/${job.job_id}`)
    } catch (err) {
      throw err
    }
  }

  const handleToggleBookmark = useCallback(async (job: Job, e: React.MouseEvent) => {
    e.stopPropagation()
    const newBookmarked = !job.bookmarked
    // Optimistic update
    updateJob(job.job_id, { bookmarked: newBookmarked })
    try {
      await updateJobApi(job.job_id, { bookmarked: newBookmarked })
    } catch (err) {
      // Revert on error
      updateJob(job.job_id, { bookmarked: job.bookmarked })
    }
  }, [updateJob])

  function handleNextPage() {
    if (nextPage) {
      setPageHistory([...pageHistory, currentPage || ''])
      setCurrentPage(nextPage)
    }
  }

  function handlePrevPage() {
    if (pageHistory.length > 0) {
      const newHistory = [...pageHistory]
      const prevPage = newHistory.pop()
      setPageHistory(newHistory)
      setCurrentPage(prevPage || undefined)
    }
  }

  function getStatusBadge(status: string) {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
    }
    return styles[status] || 'bg-gray-100 text-gray-800'
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Create Job Card - Always visible at top */}
        <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="w-full px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <div className="text-left">
                <h2 className="font-semibold text-gray-900">Create New Job</h2>
                <p className="text-sm text-gray-500">Generate Instagram story content with AI</p>
              </div>
            </div>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${showCreateForm ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showCreateForm && (
            <div className="px-6 pb-6 border-t border-gray-100">
              <div className="pt-4">
                <CreateJobForm onSubmit={handleCreateJob} />
              </div>
            </div>
          )}
        </div>

        {/* Header with title and actions */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Jobs</h1>
            <p className="text-gray-600">Your content generation jobs</p>
          </div>
          <div className="flex gap-3">
            {nocodbJobsViewUrl && (
              <a
                href={nocodbJobsViewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                View Database
              </a>
            )}
            <RefreshButton
              onClick={() => setRefreshKey((k) => k + 1)}
              loading={loading}
              size="md"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <SearchFilter
            value={search}
            onChange={setSearch}
            placeholder="Search by title, prompt, or notes..."
            className="flex-1 max-w-md"
          />
          <BookmarkToggle
            active={bookmarkedOnly}
            onChange={setBookmarkedOnly}
          />
        </div>

        {error && (
          <ErrorMessage
            message={error}
            className="mb-6"
            onDismiss={() => setError(null)}
          />
        )}

        {loading && jobs.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading jobs...</p>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <p className="text-gray-600 mb-4">
              {jobs.length === 0
                ? 'No jobs yet. Create your first job to get started!'
                : 'No jobs match your filters.'}
            </p>
            {jobs.length === 0 && (
              <LoadingButton
                onClick={() => setShowCreateForm(true)}
                variant="primary"
                size="lg"
              >
                Create Job
              </LoadingButton>
            )}
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-10 px-4 py-3"></th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title / Prompt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progress
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Approved
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Credits
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredJobs.map((job) => (
                    <tr
                      key={job.job_id}
                      onClick={() => navigate(`/jobs/${job.job_id}`)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-4 py-4">
                        <button
                          onClick={(e) => handleToggleBookmark(job, e)}
                          className="text-gray-400 hover:text-amber-500 transition-colors"
                        >
                          <svg
                            className={`w-5 h-5 ${job.bookmarked ? 'fill-amber-500 text-amber-500' : ''}`}
                            fill={job.bookmarked ? 'currentColor' : 'none'}
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                            />
                          </svg>
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                          {job.title || 'Untitled Job'}
                        </div>
                        <div className="text-xs text-gray-500 max-w-xs truncate">
                          {job.content_prompt}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(job.status)}`}>
                          {job.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {job.completed_results}/{job.total_results}
                        {job.failed_results > 0 && (
                          <span className="text-red-500 ml-1">({job.failed_results} failed)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {job.approved_count !== undefined ? (
                          <span className="text-green-600 font-medium">
                            {job.approved_count}/{job.total_results}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {job.credits_charged.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(job.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-gray-600">
                Showing {filteredJobs.length} jobs
              </div>
              <div className="flex gap-2">
                <LoadingButton
                  variant="secondary"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={pageHistory.length === 0}
                >
                  Previous
                </LoadingButton>
                <LoadingButton
                  variant="secondary"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!nextPage}
                >
                  Next
                </LoadingButton>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  )
}
